//#region [IMPORT]

// === THIRD-PARTY ===
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"

// === API ===
import { on, removeListener } from "api/prokey/prokey.websocket";

//#endregion

const ContextImage = createContext();

//-----------------------------------------------------------------
export function ImageProvider( { children } ) {
  //-----------------------------------------------------------------

  //#region [VALUES]
  
  const [ source , setSource ] = useState(null);
  const [ image  , setImage  ] = useState(null);

  const [ mouse_position, setMousePosition ] = useState({x:0,y:0});

  let is_offscreen;
  const [ ref_canvas, setRefCanvas ] = useState(null);
  const [ size , setSize ] = useState({width:0, height:0})

  const ref_subscribers = useRef( new Set() );
  const ref_surface     = useRef(null); // { canvas, context, is_offscreen, dpr, width, height }
  const raf_state       = useRef({ scheduled: false });

  const withContext  = useCallback( _withContext  , [] );
  const requestFrame = useCallback( _requestFrame , [] );
  const snapshot     = useCallback( _snapshot     , [] );
  const subscribe    = useCallback( _subscribe    , [] );
  const notify       = useCallback( _notify       , [] );

  const values = useMemo( () => ({
    source  ,
    image   ,
    width  : size.width  ,
    height : size.height ,
    snapshot,
    subscribe,
    withContext,
  }), [size.width, size.height, source, image, snapshot, subscribe, withContext]);

  //#endregion

  //#region [EFFECT]

  useEffect( () => {
    const IMAGE = 'FILE_IMAGE';
    
    //-----------------------------------------------------------------
    function handleImage(data) {
      //-----------------------------------------------------------------
      if( data.id === source?.id ) return;

      if( !data.is_selected && data.id == source?.id ) {
        setImage( null )
      } else if( data.is_focused ) {
        loadImage( data )
      }
    }

    on( IMAGE, handleImage )

    return () => {
      removeListener( IMAGE, handleImage );
    }
  }, [source] );
  
  // useEffect( () => {
  //   //-----------------------------------------------------------------
  //   function draw() {
  //     //-----------------------------------------------------------------
  //     const surface = ref_surface.current;
  //     if( !surface?.context || !image ) return;
  //     if( image.naturalWidth <= 1) return;
  // console.info('draw surface')
  //     surface.context.clearRect(        0, 0, image.naturalWidth, image.naturalHeight );
  //     surface.context.beginPath();
  //     surface.context.drawImage( image, 0, 0, image.naturalWidth, image.naturalHeight );
  //     surface.context.closePath();

  //     notify()
  //   }
    
  //   draw();
  // }, [image, ref_surface.current?.context, notify] );

  // Create canvas
  //-----------------------------------------------------------------
  useEffect( () => {
    const has_OffscreenCanvas = typeof OffscreenCanvas !== 'undefined';

    const { width, height } = size;

    let canvas, context;
    if( has_OffscreenCanvas ) {
      canvas = new OffscreenCanvas(size.width, size.height);
      context = canvas.getContext("2d", { alpha: true, desynchronized: true });
    } else {
      canvas = document.createElement('canvas')
      canvas.width  = size.width;
      canvas.height = size.height;
      context = canvas.getContext('2d');
    }

    ref_surface.current = { canvas, context, width, height };

    return () => {
      ref_surface.current = null;
    };
  }, [size.width, size.height] );

  //#endregion

  //#region [METHOD]

  //-----------------------------------------------------------------
  function loadImage( data ) {
    //-----------------------------------------------------------------
    const temp = document.createElement('img');

    temp.onload = function () {
      setSource( data );
      setImage( temp );
      if( size.width !== temp.naturalWidth || size.height !== temp.naturalHeight ) {
        setSize( { width: temp.naturalWidth, height: temp.naturalHeight } );
      }
    };

    temp.src = data.url;
  }

  // Public API for children to draw
  //-----------------------------------------------------------------
  function _withContext(fn) {
    //-----------------------------------------------------------------
    const surface = ref_surface.current;
    if (!surface || !surface.context) return;
    fn(surface.context, surface);
    requestFrame();
  }

  // Snapshot for blitting into views
  // Returns { bitmap } if OffscreenCanvas supports it, else { canvas }
  //-----------------------------------------------------------------
  async function _snapshot() {
    //-----------------------------------------------------------------
    const surface = ref_surface.current;
    if (!surface) return null;

    if (typeof surface.canvas.transferToImageBitmap === "function") {
      try {
        const bitmap = surface.canvas.transferToImageBitmap();
        console.info('bitmap')
        return { bitmap, width: surface.width, height: surface.height };
      } catch {
        // fallback to canvas path
      }
    }

    return { canvas: surface.canvas, width: surface.width, height: surface.height };
  }
  
  // A simple rAF to batch multiple draws before notifying views
  //-----------------------------------------------------------------
  function _requestFrame() {
    //-----------------------------------------------------------------
    if (raf_state.current.scheduled) return;
    raf_state.current.scheduled = true;
    
    if (typeof window !== "undefined") {
      window.requestAnimationFrame(() => {
        raf_state.current.scheduled = false;
        notify();
      });
    } else {
      // SSR no-op
      notify();
    }
  }
  
  //-----------------------------------------------------------------
  function _subscribe(cb) {
  //-----------------------------------------------------------------
    ref_subscribers.current.add(cb);
    return () => ref_subscribers.current.delete(cb);
  }
  
  //-----------------------------------------------------------------
  function _notify() {
  //-----------------------------------------------------------------
    ref_subscribers.current.forEach((cb) => {
      try {
        cb();
      } catch {
        // ignore subscriber errors
      }
    });
  }

  //#endregion

  //#region [EVENTS]

  //#endregion

  return (
    <ContextImage.Provider value={ values }>
      {children}
    </ContextImage.Provider>
  );
};

//-----------------------------------------------------------------
export function useImage() {
  //-----------------------------------------------------------------
  return useContext( ContextImage );
}


/**
 *   useEffect( () => {
    if( !ref_canvas ) return;

    let animationFrameId;
    const updateMousePosition = (event) => {
      const rect = ref_canvas.current.getBoundingClientRect();
      let x = event.clientX - rect.left;
      let y = event.clientY - rect.top;
      setMousePosition( { x, y } );
      animationFrameId = requestAnimationFrame(() => updateMousePosition(event));
    };

    const handleMouseMove = (event) => {
      cancelAnimationFrame(animationFrameId);
      updateMousePosition(event);
    };

    ref_canvas.current.addEventListener('mousemove', handleMouseMove);

    return () => {
      cancelAnimationFrame(animationFrameId);
      ref_canvas.current.removeEventListener('mousemove', handleMouseMove);
    };
  }, [ref_canvas] );
 */